/**
 * Level 2 — viewport-loaded Google Maps facade.
 *
 * The office card server-renders a static facade (a map screenshot or a pin
 * placeholder) wrapped in a link to Google Maps — fully functional with no JS.
 * When JS is present, the live interactive iframe is built inline as soon as the
 * card scrolls into view, so the map is simply *there* — no click required
 * (MOHO-123). The facade stays in the markup as the no-JS fallback and is hidden
 * (`x-show="notLoaded"`) the moment the embed mounts; its click handler remains
 * so a click that lands before the observer fires still loads immediately rather
 * than navigating away.
 *
 * The iframe is still never server-rendered: it mounts on intersection, and the
 * only pages using this block (`/contact`, `/emerge-collection`) place it well
 * below the fold. Lighthouse does not scroll, so the observer never fires during
 * an audit and the 100/100 PageSpeed budget is preserved — the same guarantee
 * the old click-gate gave, without making real users hunt for a map.
 *
 * CSP build (@alpinejs/csp) has two constraints that shape this:
 *   1. Markup may only reference property/getter/method names — no inline
 *      expressions. Hence the `notLoaded` getter + `load()` method.
 *   2. Alpine may NOT evaluate any directive on an <iframe> element. So the
 *      iframe is built imperatively here (src set as a property, no `:src`
 *      binding) and appended into a plain <div> mount — Alpine never touches it.
 *
 * The card's location lives in data attributes on the x-data root
 * (`.mh-offices__map`): data-lat / data-lng (preferred), data-q (address
 * fallback), data-title (iframe title). We read them in init() — where `$el` IS
 * the root — because `load()` fires from a @click on the child <a>, at which
 * point `$el` is that <a> and carries none of these attributes.
 */

/**
 * Load slightly before the card reaches the viewport so the embed has settled by
 * the time it is actually looked at. Kept well under a viewport height — a
 * margin large enough to catch the block from the top of the page would start
 * costing us the below-the-fold guarantee above.
 */
const ROOT_MARGIN = '200px';

export default function mapEmbed() {
    return {
        loaded: false,
        lat: '',
        lng: '',
        query: '',
        title: 'Map',
        observer: null,

        init() {
            // `$el` is only the root during init — from a @click it's the <a>.
            // Hold onto it so the observer below watches the card, not whatever
            // element happened to trigger the call.
            const root = this.$el;

            this.lat = root.dataset.lat || '';
            this.lng = root.dataset.lng || '';
            this.query = root.dataset.q || '';
            this.title = root.dataset.title || 'Map';

            this.watchViewport(root);
        },

        /**
         * Feature-detect, never browser-detect: without IntersectionObserver we
         * load straight away rather than falling back to a click gate, so the
         * map is still displayed by default. That immediate load waits a tick —
         * `x-ref="frame"` is registered while Alpine walks the children, which
         * hasn't happened yet during the root's own init().
         */
        watchViewport(root) {
            if (!('IntersectionObserver' in window)) {
                this.$nextTick(() => this.load());

                return;
            }

            this.observer = new IntersectionObserver(
                (entries) => {
                    if (entries.some((entry) => entry.isIntersecting)) {
                        this.load();
                    }
                },
                { rootMargin: ROOT_MARGIN }
            );

            this.observer.observe(root);
        },

        /**
         * Swup replaces `#swup` wholesale and Alpine.destroyTree() runs over the
         * outgoing DOM, so an observer left watching a detached node would leak
         * across every navigation.
         */
        disconnect() {
            if (this.observer) {
                this.observer.disconnect();
                this.observer = null;
            }
        },

        get notLoaded() {
            return !this.loaded;
        },

        /**
         * Build the interactive embed URL. With coordinates, `ll=` is what
         * actually centres the map (the endpoint redirects to an embed whose pb
         * carries an explicit centre, !2d<lng>!3d<lat>); `q=` on its own is a
         * *search*-type embed with no centre and Google can't geocode a bare
         * "lat,lng" search string → zoomed-out world view. So pair `ll` (centre)
         * with `q` (drops the marker). No coordinates → fall back to an address
         * search, which geocodes reliably. Key-free `output=embed` endpoint.
         *
         * Host is `www.google.com`, NOT the `maps.google.com` alias: the endpoint
         * 302s to `https://www.google.com/maps/embed?pb=…`, and CSP re-checks the
         * redirect target, so the alias would need BOTH hosts in `frame-src`.
         * Framing here is what needs allowlisting — the other maps on the site
         * (build zones, packages) are the Maps JS API, covered by script/img/
         * connect-src. See ContentSecurityPolicy::groups() core `frame-src`.
         */
        get embedSrc() {
            if (this.lat && this.lng) {
                const coords = `${this.lat},${this.lng}`;

                return `https://www.google.com/maps?ll=${coords}&q=${coords}&z=15&hl=en&output=embed`;
            }

            return `https://www.google.com/maps?q=${encodeURIComponent(this.query)}&z=15&hl=en&output=embed`;
        },

        load() {
            if (this.loaded) {
                return;
            }

            // No mount means the tree is mid-teardown (or the markup changed) —
            // bail without flipping `loaded`, which would hide the facade and
            // leave the card blank.
            const mount = this.$refs.frame;

            if (!mount) {
                return;
            }

            this.loaded = true;
            this.disconnect();

            const frame = document.createElement('iframe');
            frame.src = this.embedSrc;
            frame.title = this.title;
            frame.loading = 'lazy';
            frame.referrerPolicy = 'no-referrer-when-downgrade';
            frame.allowFullscreen = true;
            mount.appendChild(frame);
        },

        destroy() {
            this.disconnect();
        },
    };
}
